<template>
    <div class="d-flex flex-column" style="gap: 0.625rem">
        <div class="d-flex flex-column">
            <div v-if="gap.section_name" class="detail-list-item">
                <span class="label">{{
                    $t('gaps.shared.fields.section')
                }}</span>
                <strong class="value">{{ gap.section_name }}</strong>
            </div>
            <div class="detail-list-item">
                <span v-if="!isEditingPriority" class="label">{{
                    $t('actionItems.shared.fields.priority')
                }}</span>
                <form
                    v-if="isEditingPriority"
                    class="priority-edit-form"
                    @submit.prevent="savePriority"
                >
                    <Field
                        v-model="draftPriority"
                        class="priority-field"
                        type="select"
                        :label="$t('actionItems.shared.fields.priority')"
                        :params="priorityFieldParams"
                        :disabled="prioritySaving"
                        :loading="prioritySaving"
                    />
                    <IconButton
                        preset="check"
                        type="submit"
                        variant="primary"
                        :tooltip="$t('common.actions.save')"
                        :aria-label="$t('common.actions.save')"
                        :disabled="prioritySaving || !canSavePriority"
                    />
                    <IconButton
                        icon-class="fi-x"
                        type="button"
                        variant="white"
                        :tooltip="$t('common.actions.cancel')"
                        :aria-label="$t('common.actions.cancel')"
                        :disabled="prioritySaving"
                        @click="cancelPriorityEdit"
                    />
                </form>
                <span v-else class="value priority-value">
                    <template v-if="gap.priority">
                        <ToolTip>
                            <template #tooltip-trigger>
                                <i
                                    :class="[
                                        'fi fi-lg',
                                        getPriorityIcon(gap.priority),
                                        getPriorityIconClass(gap.priority),
                                    ]"
                                ></i>
                            </template>
                            <template #tooltip-content>
                                {{
                                    gap.priorityLabel ||
                                    getPriorityLabelText(gap.priority)
                                }}
                            </template>
                        </ToolTip>
                        <strong>{{
                            gap.priorityLabel ||
                            getPriorityLabelText(gap.priority)
                        }}</strong>
                    </template>
                    <IconButton
                        v-if="canEditPriority"
                        preset="edit"
                        variant="white"
                        :tooltip="$t('gaps.shared.priorityOverride.change')"
                        :aria-label="$t('gaps.shared.priorityOverride.change')"
                        :disabled="prioritySaving"
                        @click="startPriorityEdit"
                    />
                </span>
            </div>
            <div
                v-if="gap.status != null && gap.status !== ''"
                class="detail-list-item"
            >
                <span class="label">{{ $t('gaps.shared.fields.status') }}</span>
                <span class="value">
                    <StatusBadge
                        :label="
                            gap.statusLabel || getGapStatusLabel(gap.status)
                        "
                        :variant="getGapStatusVariant(gap.status)"
                    />
                </span>
            </div>
            <div class="detail-list-item">
                <span class="label">{{ $t('gaps.shared.fields.gap') }}</span>
                <strong class="value">{{ gapText }}</strong>
            </div>
            <div class="detail-list-item">
                <span class="label">{{
                    $t('gaps.shared.fields.recommendation')
                }}</span>
                <strong class="value">{{ recommendationText }}</strong>
            </div>
        </div>

        <div class="linked-action-points-block">
            <span class="label">{{
                $t('gaps.shared.linkedActionItems.title')
            }}</span>
            <p
                v-if="linkedActionPointsLoading"
                class="text-muted mb-0 linked-action-points-muted"
            >
                {{ $t('gaps.shared.linkedActionItems.loading') }}
            </p>
            <template v-else>
                <p
                    v-if="linkedActionPointsTruncated"
                    class="text-muted small mb-2 linked-action-points-muted"
                >
                    {{
                        $t('gaps.shared.linkedActionItems.truncated', {
                            count: linkedActionPoints.length,
                        })
                    }}
                </p>
                <p
                    v-if="!linkedActionPoints.length"
                    class="text-muted mb-0 linked-action-points-muted"
                >
                    {{ $t('gaps.shared.linkedActionItems.empty') }}
                </p>
                <ul v-else class="list-unstyled mb-0 linked-action-points-list">
                    <li
                        v-for="ap in linkedActionPoints"
                        :key="ap.id"
                        class="linked-ap-row"
                    >
                        <div class="linked-ap-main">
                            <div class="linked-ap-title">
                                {{ actionPointSummary(ap) }}
                            </div>
                            <div class="linked-ap-meta">
                                <ToolTip>
                                    <template #tooltip-trigger>
                                        <i
                                            :class="[
                                                'fi fi-sm',
                                                getProgressIcon(ap.progress),
                                                getProgressIconClass(
                                                    ap.progress
                                                ),
                                            ]"
                                        ></i>
                                    </template>
                                    <template #tooltip-content>
                                        {{ getProgressLabelText(ap.progress) }}
                                    </template>
                                </ToolTip>
                                <ToolTip>
                                    <template #tooltip-trigger>
                                        <i
                                            :class="[
                                                'fi fi-sm',
                                                getPriorityIcon(ap.priority),
                                                getPriorityIconClass(
                                                    ap.priority
                                                ),
                                            ]"
                                        ></i>
                                    </template>
                                    <template #tooltip-content>
                                        {{ getPriorityLabelText(ap.priority) }}
                                    </template>
                                </ToolTip>
                                <span
                                    v-if="ap.deadline"
                                    class="linked-ap-deadline"
                                >
                                    {{ formatDeadline(ap.deadline) }}
                                </span>
                                <span
                                    v-if="ap.accountable_id"
                                    class="linked-ap-accountable text-muted"
                                >
                                    {{ getAccountableName(ap.accountable_id) }}
                                </span>
                            </div>
                        </div>
                        <IconButton
                            preset="view"
                            :tooltip="$t('gaps.shared.linkedActionItems.view')"
                            @click="$emit('open-action-point', ap)"
                        />
                    </li>
                </ul>
            </template>
        </div>
    </div>
</template>

<script>
import Field from '@/components/Field.vue';
import ToolTip from '@/components/fields/ToolTip.vue';
import IconButton from '@/components/IconButton.vue';
import StatusBadge from '@/components/StatusBadge.vue';
import {
    getAuditGapStatusLabel,
    getAuditGapStatusVariant,
} from '@/utils/auditGapStatus';
import { formatDisplayDate } from '@/utils/dateUtils';
import {
    getPriorityIcon,
    getPriorityIconClass,
    getPriorityLabel,
    getProgressIcon,
    getProgressIconClass,
    getProgressLabel,
} from '@/utils/actionItemDisplay';
import { AUDIT_GAP_PRIORITY_VALUES } from '@/utils/auditOverview';
export default {
    name: 'ViewAuditGap',
    components: { Field, ToolTip, IconButton, StatusBadge },
    props: {
        gap: {
            type: Object,
            required: true,
        },
        linkedActionPoints: {
            type: Array,
            default: () => [],
        },
        linkedActionPointsLoading: {
            type: Boolean,
            default: false,
        },
        linkedActionPointsTruncated: {
            type: Boolean,
            default: false,
        },
        prioritySaving: {
            type: Boolean,
            default: false,
        },
        canEditPriority: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['open-action-point', 'update-priority'],
    data() {
        return {
            isEditingPriority: false,
            draftPriority: '',
        };
    },
    computed: {
        priorityOptions() {
            return AUDIT_GAP_PRIORITY_VALUES.map((value) => ({
                value,
                label: this.getPriorityLabelText(value),
            }));
        },
        priorityFieldParams() {
            return {
                options: this.priorityOptions,
                allowEmpty: false,
            };
        },
        canSavePriority() {
            const currentPriority = String(this.gap?.priority || '')
                .trim()
                .toUpperCase();
            return (
                AUDIT_GAP_PRIORITY_VALUES.includes(this.draftPriority) &&
                this.draftPriority !== currentPriority
            );
        },
        gapText() {
            const text = String(this.gap?.gap ?? '').trim();
            return text || '—';
        },
        recommendationText() {
            const text = String(this.gap?.recommendation ?? '').trim();
            return text || '—';
        },
    },
    watch: {
        gap(nextGap, prevGap) {
            if (nextGap?.id !== prevGap?.id) {
                this.cancelPriorityEdit();
                return;
            }
            const nextPriority = String(nextGap?.priority || '')
                .trim()
                .toUpperCase();
            if (this.isEditingPriority && nextPriority === this.draftPriority) {
                this.cancelPriorityEdit();
            }
        },
    },
    methods: {
        getPriorityIcon,
        getPriorityIconClass,
        getProgressIcon,
        getProgressIconClass,
        getGapStatusVariant: getAuditGapStatusVariant,
        translate(key, values = null) {
            return values ? this.$t(key, values) : this.$t(key);
        },
        getPriorityLabelText(priority) {
            return getPriorityLabel(priority, this.translate);
        },
        getProgressLabelText(progress) {
            return getProgressLabel(progress, this.translate);
        },
        getGapStatusLabel(status) {
            return getAuditGapStatusLabel(status, this.translate);
        },
        actionPointSummary(ap) {
            const text = String(ap?.action_point ?? '').trim();
            return text || '—';
        },
        formatDeadline(dateString) {
            if (!dateString) return '';
            return formatDisplayDate(dateString);
        },
        startPriorityEdit() {
            this.draftPriority = String(this.gap?.priority || '')
                .trim()
                .toUpperCase();
            if (!this.draftPriority) {
                this.draftPriority = AUDIT_GAP_PRIORITY_VALUES[0];
            }
            this.isEditingPriority = true;
        },
        cancelPriorityEdit() {
            this.isEditingPriority = false;
            this.draftPriority = '';
        },
        savePriority() {
            if (this.prioritySaving || !this.canSavePriority) return;
            const priority = String(this.draftPriority || '')
                .trim()
                .toUpperCase();
            if (!priority) return;
            this.$emit('update-priority', priority);
        },
        getAccountableName(accountableId) {
            if (!accountableId) return '';
            const employee =
                this.$employeesStore?.getEmployeeById(accountableId);
            if (employee) {
                return (
                    `${employee.first_name || ''} ${
                        employee.last_name || ''
                    }`.trim() || this.$t('common.fallbacks.unknown')
                );
            }
            return this.$t('common.fallbacks.unknown');
        },
    },
};
</script>

<style scoped>
.detail-list-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.625rem 0;
    border-bottom: 1px solid var(--neutral-600, #e6e6e6);
}

.label {
    font-size: 0.875rem;
    color: var(--neutral-700);
    font-weight: 400;
}

.value {
    font-size: 0.9375rem;
    color: var(--neutral-900);
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.priority-value {
    gap: var(--space-2);
}

.priority-edit-form {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    align-items: end;
    gap: var(--space-2);
}

.priority-field {
    min-width: 0;
}

.priority-low {
    color: var(--success);
}

.priority-medium {
    color: var(--warning);
}

.priority-high {
    color: var(--danger);
}

.priority-legislated {
    color: var(--danger);
}

.priority-trivial {
    color: var(--neutral-800);
}

.progress-success {
    color: var(--success);
}

.progress-warning {
    color: var(--warning);
}

.progress-danger {
    color: var(--danger);
}

.linked-action-points-heading {
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 var(--space-3);
    color: var(--neutral-900);
}

.linked-action-points-muted {
    font-size: 0.875rem;
}

.linked-action-points-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
}

.linked-ap-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-3);
    padding: var(--space-3);
    background-color: var(--neutral-400, #f5f5f5);
    border-radius: var(--space-3);
}

.linked-ap-main {
    flex: 1;
    min-width: 0;
}

.linked-ap-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--neutral-900);
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.linked-ap-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-2);
    margin-top: var(--space-2);
    font-size: 0.8125rem;
}

.linked-ap-deadline {
    color: var(--neutral-800);
}

.linked-ap-accountable {
    font-size: 0.8125rem;
}
</style>
